
import { useState, useCallback, useEffect } from 'react';

/**
 * Custom hook for managing contribution visibility state
 * with optional persistence to localStorage
 */
export const useContributionVisibility = (
  initialState: boolean = false, 
  storageKey: string = 'showContribution'
) => {
  // Initialize state from localStorage if available, but default to false (hidden)
  const [isVisible, setIsVisible] = useState<boolean>(() => {
    try {
      const storedValue = localStorage.getItem(storageKey);
      // Default to false to hide contributions by default
      return storedValue ? JSON.parse(storedValue) : false;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      // Default to false if error occurs
      return false;
    }
  });

  // Toggle visibility state
  const toggleVisibility = useCallback(() => {
    setIsVisible(prev => !prev);
  }, []);

  // Show contribution
  const showContribution = useCallback(() => {
    setIsVisible(true);
  }, []);

  // Hide contribution
  const hideContribution = useCallback(() => {
    setIsVisible(false);
  }, []);

  // Persist state changes to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(isVisible));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [isVisible, storageKey]);

  return {
    isVisible,
    toggleVisibility,
    showContribution, 
    hideContribution
  };
};
